import { notify } from "@general-backoffice/core";
import type { Invoice, TaxProfile, User } from "@investhinks/api-client";
import { saveAs } from "file-saver";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import { invoicingEndpoints } from "../../../../api";

type Referrer = Pick<User, "id" | "name" | "email" | "country"> & { taxProfile?: Pick<TaxProfile, "taxProfileComplete" | "taxIdNumber" | "taxIdType" | "irpfRate"> | null };
type LocalInvoice = Pick<Invoice, "id" | "invoiceNumber" | "invoiceDate" | "issuerName" | "issuerTaxId" | "referrerType" | "baseAmount" | "vatAmount" | "irpfAmount" | "netPayable" | "status"> & { isTest?: boolean };

interface ReferrerTypeConfig {
  label: string;
  color: string;
}

const REFERRER_TYPES: Record<string, ReferrerTypeConfig> = {
  SPANISH_COMPANY: { label: "Spanish Company", color: "primary" },
  SPANISH_AUTONOMO: { label: "Spanish Autónomo", color: "info" },
  SPANISH_AUTONOMO_EMPRESARIAL: { label: "Autónomo Empresarial", color: "info" },
  SPANISH_AUTONOMO_PROFESIONAL: { label: "Autónomo Profesional", color: "info" },
  SPANISH_PARTICULAR: { label: "Spanish Particular", color: "secondary" },
  EU_BUSINESS: { label: "EU Business", color: "success" },
  EU_PRIVATE: { label: "EU Private", color: "secondary" },
  NON_EU_BUSINESS: { label: "Non-EU Business", color: "warning" },
  NON_EU_PRIVATE: { label: "Non-EU Private", color: "secondary" },
};

const InvoiceTesting: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "adminPages.invoiceTesting" });

  // Referrer search state
  const [referrers, setReferrers] = useState<Referrer[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoadingReferrers, setIsLoadingReferrers] = useState(false);

  // Invoice generation state
  const [selectedReferrer, setSelectedReferrer] = useState<Referrer | null>(null);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);

  // Invoices list state
  const [invoices, setInvoices] = useState<LocalInvoice[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [showTestOnly, setShowTestOnly] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Get available years
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => 2020 + i);

  // Search referrers
  const searchReferrers = useCallback(async () => {
    if (!searchQuery || searchQuery.length < 2) {
      setReferrers([]);
      return;
    }

    setIsLoadingReferrers(true);
    try {
      const { data } = await invoicingEndpoints.adminListReferrers({
        search: searchQuery,
        limit: 10,
      });
      // Map api-client response to local Referrer type
      setReferrers(
        (data || []).map((r) => ({
          id: typeof r.id === "string" ? parseInt(r.id, 10) : r.id,
          name: r.name,
          email: r.email,
          country: r.country,
        }))
      );
    } catch (err: unknown) {
      console.error("Failed to search referrers:", err);
      const errorMessage = err instanceof Error ? err.message : String(t("searchError"));
      notify.error(errorMessage);
    } finally {
      setIsLoadingReferrers(false);
    }
  }, [searchQuery, t]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchReferrers();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchReferrers]);

  // Load invoices
  const loadInvoices = useCallback(async () => {
    setIsLoadingInvoices(true);
    try {
      const { data } = await invoicingEndpoints.adminListInvoices({
        year: selectedYear,
        isTest: showTestOnly ? "true" : undefined,
        limit: 50,
      });
      // Map api-client Invoice to LocalInvoice type
      setInvoices(
        (data || []).map((inv) => ({
          id: typeof inv.id === "string" ? parseInt(inv.id, 10) : inv.id,
          invoiceNumber: inv.invoiceNumber,
          invoiceDate: inv.invoiceDate,
          issuerName: inv.issuer?.name,
          issuerTaxId: inv.issuer?.taxId,
          baseAmount: inv.baseAmount,
          vatAmount: inv.vatAmount,
          irpfAmount: inv.irpfAmount,
          netPayable: inv.netPayable,
          status: inv.status,
          isTest: inv.invoiceNumber?.startsWith("TEST-"),
        }))
      );
    } catch (err: unknown) {
      console.error("Failed to load invoices:", err);
      const errorMessage = err instanceof Error ? err.message : String(t("loadError"));
      notify.error(errorMessage);
    } finally {
      setIsLoadingInvoices(false);
    }
  }, [selectedYear, showTestOnly, t]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Preview invoice
  const handlePreview = async () => {
    if (!selectedReferrer) {
      notify.error(t("selectReferrerError"));
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      notify.error(t("invalidAmountError"));
      return;
    }

    setIsPreviewing(true);
    try {
      // Download PDF preview
      const blob = await invoicingEndpoints.adminPreviewInvoice({
        referrerId: selectedReferrer.id,
        amount: parseFloat(amount),
        description: description || undefined,
      });
      saveAs(blob, `invoice_preview_${selectedReferrer.id}.pdf`);
      notify.success(t("previewSuccess"));
    } catch (err: unknown) {
      console.error("Failed to preview invoice:", err);
      const errorMessage = err instanceof Error ? err.message : String(t("previewError"));
      notify.error(errorMessage);
    } finally {
      setIsPreviewing(false);
    }
  };

  // Generate test invoice
  const handleGenerate = async () => {
    if (!selectedReferrer) {
      notify.error(t("selectReferrerError"));
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      notify.error(t("invalidAmountError"));
      return;
    }

    if (!window.confirm(t("generateConfirm"))) return;

    setIsGenerating(true);
    try {
      const { data: invoice } = await invoicingEndpoints.adminGenerateTestInvoice({
        referrerId: selectedReferrer.id,
        amount: parseFloat(amount),
        description: description || undefined,
      });

      notify.success(`${t("generateSuccess")}: ${invoice?.invoiceNumber}`);
      loadInvoices();

      // Reset form
      setAmount("");
      setDescription("");
    } catch (err: unknown) {
      console.error("Failed to generate invoice:", err);
      const errorMessage = err instanceof Error ? err.message : String(t("generateError"));
      notify.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  // Download invoice PDF
  const handleDownloadPDF = async (invoice: LocalInvoice) => {
    try {
      const blob = await invoicingEndpoints.adminDownloadInvoicePDF(invoice.id);
      saveAs(blob, `invoice_${invoice.invoiceNumber}.pdf`);
      notify.success(t("downloadSuccess"));
    } catch (err: unknown) {
      console.error("Failed to download PDF:", err);
      const errorMessage = err instanceof Error ? err.message : String(t("downloadError"));
      notify.error(errorMessage);
    }
  };

  // Delete test invoice
  const handleDelete = async (invoice: LocalInvoice) => {
    if (!invoice.isTest) {
      notify.error(t("cannotDeleteNonTest"));
      return;
    }
    if (!window.confirm(t("deleteConfirm"))) return;

    try {
      await invoicingEndpoints.adminDeleteTestInvoice(invoice.id);
      notify.success(t("deleteSuccess"));
      loadInvoices();
    } catch (err: unknown) {
      console.error("Failed to delete invoice:", err);
      const errorMessage = err instanceof Error ? err.message : String(t("deleteError"));
      notify.error(errorMessage);
    }
  };

  // Format amount
  const formatAmount = (amount: number | undefined | null): string => {
    if (amount == null) return "-";
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  // Get referrer type badge
  const getReferrerTypeBadge = (type: string | null | undefined) => {
    const config = REFERRER_TYPES[type || ""] || { label: type || "Unknown", color: "secondary" };
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  // Get status badge
  const getStatusBadge = (status: string | null | undefined) => {
    const colors: Record<string, string> = {
      pending: "warning",
      issued: "success",
      paid: "primary",
      cancelled: "danger",
    };
    return <Badge color={colors[status || ""] || "secondary"}>{status || "unknown"}</Badge>;
  };

  return (
    <Container className="mt-4" fluid>
      {/* Generate Invoice Card */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow">
            <CardHeader className="bg-gradient-dark text-white">
              <h5 className="mb-0">
                <i className="fas fa-file-invoice mr-2"></i>
                {t("generateTitle") || "Generate Test Invoice"}
              </h5>
            </CardHeader>
            <CardBody>
              {/* Referrer Search */}
              <Row className="mb-3">
                <Col md={6}>
                  <FormGroup>
                    <Label for="invoice-testing-field-1">{t("searchReferrer") || "Search Referrer"}</Label>
                    <InputGroup>
                      <Input
                        id="invoice-testing-field-1"
                        type="text"
                        placeholder={t("searchPlaceholder") || "Search by username, email, or name..."}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <InputGroupAddon addonType="append">
                        <InputGroupText>
                          {isLoadingReferrers ? <Spinner size="sm" /> : <i className="fas fa-search"></i>}
                        </InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </FormGroup>

                  {/* Referrer Results */}
                  {referrers.length > 0 && (
                    <div className="border rounded p-2 mb-3" style={{ maxHeight: "200px", overflowY: "auto" }}>
                      {referrers.map((ref) => (
                        <div
                          key={ref.id}
                          className={`p-2 rounded cursor-pointer ${
                            selectedReferrer?.id === ref.id ? "bg-primary text-white" : "hover-bg-light"
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => setSelectedReferrer(ref)}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{ref.name || ref.email}</strong>
                              <small className="d-block text-muted">{ref.email}</small>
                            </div>
                            <div className="text-right">
                              {ref.taxProfile?.taxProfileComplete ? (
                                <Badge color="success" size="sm">
                                  <i className="fas fa-check mr-1"></i>
                                  {t("profileComplete") || "Complete"}
                                </Badge>
                              ) : (
                                <Badge color="warning" size="sm">
                                  <i className="fas fa-exclamation-triangle mr-1"></i>
                                  {t("profileIncomplete") || "Incomplete"}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Col>

                {/* Selected Referrer Details */}
                <Col md={6}>
                  {selectedReferrer && (
                    <Card className="bg-light">
                      <CardBody>
                        <h6 className="mb-3">
                          <i className="fas fa-user mr-2"></i>
                          {t("selectedReferrer") || "Selected Referrer"}
                        </h6>
                        <Table size="sm" borderless>
                          <tbody>
                            <tr>
                              <td className="font-weight-bold">{t("name") || "Name"}:</td>
                              <td>{selectedReferrer.name || "-"}</td>
                            </tr>
                            <tr>
                              <td className="font-weight-bold">{t("email") || "Email"}:</td>
                              <td>{selectedReferrer.email}</td>
                            </tr>
                            <tr>
                              <td className="font-weight-bold">{t("country") || "Country"}:</td>
                              <td>{selectedReferrer.country || "-"}</td>
                            </tr>
                            <tr>
                              <td className="font-weight-bold">{t("taxId") || "Tax ID"}:</td>
                              <td>
                                {selectedReferrer.taxProfile?.taxIdNumber || "-"}
                                {selectedReferrer.taxProfile?.taxIdType && (
                                  <small className="text-muted ml-1">({selectedReferrer.taxProfile.taxIdType})</small>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td className="font-weight-bold">{t("irpfRate") || "IRPF"}:</td>
                              <td>
                                {selectedReferrer.taxProfile?.irpfRate != null
                                  ? `${(selectedReferrer.taxProfile.irpfRate * 100).toFixed(0)}%`
                                  : "-"}
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  )}
                </Col>
              </Row>

              {/* Invoice Details */}
              <Row>
                <Col md={3}>
                  <FormGroup>
                    <Label for="invoice-testing-field-2">{t("amount") || "Amount (EUR)"}</Label>
                    <Input id="invoice-testing-field-2"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="100.00"
                    />
                  </FormGroup>
                </Col>
                <Col md={5}>
                  <FormGroup>
                    <Label for="invoice-testing-field-3">{t("description") || "Description (optional)"}</Label>
                    <Input id="invoice-testing-field-3"
                      type="text"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t("descriptionPlaceholder") || "Affiliate commission"}
                    />
                  </FormGroup>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <Button
                    color="info"
                    onClick={handlePreview}
                    disabled={isPreviewing || !selectedReferrer}
                    className="mb-3 mr-2"
                  >
                    {isPreviewing ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        {t("previewing") || "Previewing..."}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-eye mr-2"></i>
                        {t("preview") || "Preview PDF"}
                      </>
                    )}
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleGenerate}
                    disabled={isGenerating || !selectedReferrer}
                    className="mb-3"
                  >
                    {isGenerating ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        {t("generating") || "Generating..."}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-plus mr-2"></i>
                        {t("generate") || "Generate Test Invoice"}
                      </>
                    )}
                  </Button>
                </Col>
              </Row>

              <Alert color="warning" className="mb-0">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                <strong>{t("warning") || "Warning"}:</strong>{" "}
                {t("testInvoiceWarning") ||
                  "Test invoices are saved to the database but will NOT be submitted to Verifactu. Use for testing tax calculations and PDF generation only."}
              </Alert>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Invoices List */}
      <Row>
        <Col>
          <Card className="shadow">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-list mr-2"></i>
                {t("invoicesTitle") || "Generated Invoices"}
              </h5>
              <div className="d-flex align-items-center">
                <FormGroup check className="mr-3 mb-0">
                  <Label check>
                    <Input type="checkbox" checked={showTestOnly} onChange={(e) => setShowTestOnly(e.target.checked)} />{" "}
                    {t("showTestOnly") || "Show test invoices only"}
                  </Label>
                </FormGroup>
                <Input
                  type="select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  style={{ width: "100px" }}
                  className="mr-2"
                >
                  {availableYears.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Input>
                <Button color="link" onClick={loadInvoices} disabled={isLoadingInvoices}>
                  <i className={`fas fa-sync ${isLoadingInvoices ? "fa-spin" : ""}`}></i>
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {isLoadingInvoices ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                </div>
              ) : invoices.length === 0 ? (
                <Alert color="info" className="mb-0">
                  {t("noInvoices") || "No invoices found for the selected criteria."}
                </Alert>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>{t("invoiceNumber") || "Invoice #"}</th>
                      <th>{t("date") || "Date"}</th>
                      <th>{t("issuer") || "Issuer"}</th>
                      <th>{t("type") || "Type"}</th>
                      <th className="text-right">{t("baseAmount") || "Base"}</th>
                      <th className="text-right">{t("vatAmount") || "VAT"}</th>
                      <th className="text-right">{t("irpfAmount") || "IRPF"}</th>
                      <th className="text-right">{t("netPayable") || "Net"}</th>
                      <th>{t("status") || "Status"}</th>
                      <th>{t("actions") || "Actions"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>
                          <code>{invoice.invoiceNumber}</code>
                          {invoice.isTest && (
                            <Badge color="warning" className="ml-2">
                              TEST
                            </Badge>
                          )}
                        </td>
                        <td>{invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString("es-ES") : "-"}</td>
                        <td>
                          <div>{invoice.issuerName}</div>
                          <small className="text-muted">{invoice.issuerTaxId}</small>
                        </td>
                        <td>{getReferrerTypeBadge(invoice.referrerType)}</td>
                        <td className="text-right">{formatAmount(invoice.baseAmount)}</td>
                        <td className="text-right">
                          {formatAmount(invoice.vatAmount)}
                          {(invoice.vatAmount ?? 0) > 0 && (invoice.baseAmount ?? 0) > 0 && (
                            <small className="text-muted d-block">
                              {(((invoice.vatAmount ?? 0) / (invoice.baseAmount ?? 1)) * 100).toFixed(0)}%
                            </small>
                          )}
                        </td>
                        <td className="text-right text-danger">
                          {(invoice.irpfAmount ?? 0) > 0 ? `-${formatAmount(invoice.irpfAmount)}` : "-"}
                          {(invoice.irpfAmount ?? 0) > 0 && (invoice.baseAmount ?? 0) > 0 && (
                            <small className="text-muted d-block">
                              {(((invoice.irpfAmount ?? 0) / (invoice.baseAmount ?? 1)) * 100).toFixed(0)}%
                            </small>
                          )}
                        </td>
                        <td className="text-right font-weight-bold">{formatAmount(invoice.netPayable)}</td>
                        <td>{getStatusBadge(invoice.status)}</td>
                        <td>
                          <Button
                            color="info"
                            size="sm"
                            className="mr-1"
                            onClick={() => handleDownloadPDF(invoice)}
                            title={t("downloadPDF") || "Download PDF"}
                          >
                            <i className="fas fa-file-pdf"></i>
                          </Button>
                          {invoice.isTest && (
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDelete(invoice)}
                              title={t("delete") || "Delete"}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default InvoiceTesting;
